import { useState, useCallback } from 'react';

export interface ValidationError {
  field: string;
  message: string;
}

/**
 * Shared hook for per-field validation error display.
 *
 * Usage in form hooks:
 *   const { fieldErrors, setFieldErrors, clearFieldErrors, getFieldError } = useFieldErrors();
 *
 * On 422 response:
 *   setFieldErrors(errors);  // [{field: "fc110_12", message: "客戶代號不可空白!"}]
 *
 * On mode change / successful save:
 *   clearFieldErrors();
 *
 * In form JSX:
 *   <FieldError error={getFieldError('fc110_12')} />
 *   <Input ... />
 */
export function useFieldErrors() {
  const [fieldErrors, setErrors] = useState<Record<string, string>>({});

  const setFieldErrors = useCallback((errors: ValidationError[]) => {
    const map: Record<string, string> = {};
    for (const e of errors) {
      // Normalize to lowercase (backend sends uppercase FC110_12, frontend uses fc110_12)
      const key = e.field ? e.field.toLowerCase() : '';
      if (key && !map[key]) {
        map[key] = e.message;
      }
    }
    setErrors(map);
  }, []);

  const clearFieldErrors = useCallback(() => {
    setErrors({});
  }, []);

  const getFieldError = useCallback(
    (field: string): string | undefined => fieldErrors[field],
    [fieldErrors]
  );

  const hasErrors = Object.keys(fieldErrors).length > 0;

  return { fieldErrors, setFieldErrors, clearFieldErrors, getFieldError, hasErrors };
}
